/* Banking Portal · Wave-7 panels (Feb 2026).
   Refund Quotas · Legal Queue · Treasury · Buyer & Seller score distribution ·
   Integrations status · Underwriting metrics.
   These are imported as new tabs inside Regulatory, Compliance, and Trust pages. */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Gauge, Gavel, DollarSign, TrendingUp, Plug, FileSearch,
  CheckCircle2, AlertTriangle, ShieldCheck, Award, Building2,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";

function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function fmtCompact(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1, style: "currency", currency: "USD" }).format(n);
}
function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 1 · REFUND QUOTAS
// ═════════════════════════════════════════════════════════════════════════
export function RefundQuotasPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/regulatory/refund-quotas").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="quotas-loading">Loading…</div>;

  return (
    <div data-testid="quotas-panel" className="space-y-4">
      <div className="rounded-2xl border bg-emerald-50 border-emerald-200 p-3 flex items-start gap-2" data-testid="quotas-attest">
        <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
        <span className="text-xs text-emerald-800">{data.dual_control_attestation}</span>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Tier</div>
          <div className="col-span-1 text-right">Admins</div>
          <div className="col-span-2 text-right">Daily limit</div>
          <div className="col-span-2 text-right">Single-action cap</div>
          <div className="col-span-2 text-right">Spent today</div>
          <div className="col-span-2 text-right">Used</div>
        </div>
        <div data-testid="quotas-table">
          {data.tiers.map(t => {
            const isUnlimited = t.daily_refund_usd === 0;
            const usedTone = t.used_pct_today >= 90 ? "rose" : t.used_pct_today >= 70 ? "amber" : "emerald";
            return (
              <div key={t.id} data-testid={`quotas-row-${t.id}`} className="grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center">
                <div className="col-span-3">
                  <div className="font-medium">{t.label}</div>
                  <div className="text-[10px] font-mono text-slate-500">{t.id}</div>
                </div>
                <div className="col-span-1 text-right tabular-nums">{t.admin_count}</div>
                <div className="col-span-2 text-right tabular-nums">{isUnlimited ? <Badge variant="outline" className="text-[10px]">unlimited</Badge> : fmtMoney(t.daily_refund_usd)}</div>
                <div className="col-span-2 text-right tabular-nums">{isUnlimited ? "—" : fmtMoney(t.single_action_usd)}</div>
                <div className="col-span-2 text-right tabular-nums">{fmtMoney(t.spent_today_usd)}</div>
                <div className="col-span-2 text-right">
                  {isUnlimited ? <span className="text-xs text-slate-400">—</span> : (
                    <Badge variant="outline" className={`bg-${usedTone}-50 text-${usedTone}-700 border-${usedTone}-200 text-[10px]`}>{t.used_pct_today}%</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl border bg-white p-4 text-xs text-slate-600" data-testid="quotas-actions">
        <div className="font-medium text-sm text-slate-700 mb-2">Money-action types tracked</div>
        <div className="flex flex-wrap gap-1.5">
          {data.money_actions.map(a => <span key={a} className="font-mono text-[10px] bg-slate-100 rounded px-2 py-0.5">{a}</span>)}
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 2 · LEGAL QUEUE
// ═════════════════════════════════════════════════════════════════════════
export function LegalQueuePanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/regulatory/legal-queue").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="legal-loading">Loading…</div>;

  return (
    <div data-testid="legal-panel" className="space-y-4">
      <div className="grid grid-cols-3 gap-3" data-testid="legal-kpis">
        <Kpi testid="legal-holds"    label="Active holds"        value={data.holds_total.toLocaleString()}     icon={Gavel} tone={data.holds_total ? "amber" : "emerald"} />
        <Kpi testid="legal-subpoenas" label="Subpoenas (total)"   value={data.subpoenas_total.toLocaleString()} icon={FileSearch} tone={data.subpoenas_total ? "amber" : "emerald"} />
        <Kpi testid="legal-inquiries" label="Regulatory inquiries" value={data.inquiries_total.toLocaleString()} icon={FileSearch} tone={data.inquiries_total ? "amber" : "emerald"} />
      </div>
      {!data.any_active && (
        <div data-testid="legal-attest" className="rounded-2xl border bg-emerald-50 border-emerald-200 p-4 flex items-start gap-2.5">
          <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-900">{data.attestation_text}</div>
        </div>
      )}
      {data.holds.length > 0 && (
        <div className="rounded-2xl border bg-white overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Active legal holds</div>
          {data.holds.slice(0, 30).map(h => (
            <div key={h.id} className="px-4 py-2 border-b border-slate-100 text-sm">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-[10px]">{h.kind || "hold"}</Badge>
                <span className="text-xs text-slate-500">{h.opened_at}</span>
              </div>
              <div className="text-xs text-slate-700 mt-1 truncate">{h.summary}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 3 · TREASURY POSITION (rendered inside Trust > Operations)
// ═════════════════════════════════════════════════════════════════════════
export function TreasuryPositionCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/trust/treasury-position").then(r => setData(r.data)); }, []);
  if (!data) return null;
  const tone = data.runway_health === "concern" ? "rose" : data.runway_health === "watch" ? "amber" : "emerald";

  return (
    <div className="rounded-2xl border bg-white p-4" data-testid="treasury-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          <DollarSign className="w-4 h-4 text-emerald-500" /> Verqo treasury position
        </div>
        <Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 uppercase text-[10px]`} data-testid="treasury-health">{data.runway_health}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi testid="treasury-cash"     label="Platform cash"   value={fmtCompact(data.platform_cash_usd)}  sub={fmtMoney(data.platform_cash_usd)} tone="emerald" />
        <Kpi testid="treasury-inflow"   label="Inflow · 30d"    value={fmtCompact(data.inflow_30d_usd)}     tone="emerald" />
        <Kpi testid="treasury-outflow"  label="Outflow · 30d"   value={fmtCompact(data.outflow_30d_usd)}    tone="rose" />
        <Kpi testid="treasury-burn"     label="Monthly burn"    value={data.monthly_burn_usd ? fmtCompact(data.monthly_burn_usd) : "—"} tone={data.monthly_burn_usd ? "amber" : "emerald"} />
        <Kpi testid="treasury-runway"   label="Runway"          value={data.runway_months ? `${data.runway_months}m` : "∞"} sub={data.runway_months ? "at current burn" : "net positive cash"} tone={tone} />
      </div>
      <div className="text-[11px] text-slate-500 mt-2">90-day platform revenue · {fmtMoney(data.revenue_90d_usd)}</div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 4 + 5 · SCORE DISTRIBUTION (combined buyer + seller)
// ═════════════════════════════════════════════════════════════════════════
export function ScoreDistributionPanel() {
  const [b, setB] = useState(null);
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/compliance/buyer-score-distribution").then(r => setB(r.data));
    api.get("/partner/banking/compliance/seller-trust-distribution").then(r => setS(r.data));
  }, []);

  return (
    <div className="space-y-4" data-testid="score-dist-panel">
      <div className="rounded-2xl border bg-white p-4" data-testid="buyer-dist-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" /> Buyer Trade Score distribution
          </div>
          {b && <span className="text-[11px] text-slate-500">sample {b.sample_size}</span>}
        </div>
        {b && b.sample_size > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-3" data-testid="buyer-bands">
              {Object.entries(b.bands).map(([k, n]) => (
                <Kpi key={k} testid={`buyer-band-${k.toLowerCase()}`} label={k} value={n.toLocaleString()} tone={k === "Premier" ? "emerald" : k === "Establishing" ? "sky" : k === "Building" ? "amber" : "rose"} />
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2 mt-3 text-xs">
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P10</div><span className="tabular-nums font-medium">{b.p10}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P50</div><span className="tabular-nums font-medium">{b.p50}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P90</div><span className="tabular-nums font-medium">{b.p90}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Min/Max</div><span className="tabular-nums">{b.min}/{b.max}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Mean</div><span className="tabular-nums">{b.mean}</span></div>
            </div>
          </>
        ) : <div className="text-sm text-slate-500" data-testid="buyer-empty">No buyer scores yet.</div>}
      </div>

      <div className="rounded-2xl border bg-white p-4" data-testid="seller-dist-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <Award className="w-4 h-4" /> Seller behavioural trust distribution
          </div>
          {s && <span className="text-[11px] text-slate-500">sample {s.sample_size}</span>}
        </div>
        {s && s.sample_size > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-3" data-testid="seller-bands">
              {Object.entries(s.bands).map(([k, n]) => (
                <Kpi key={k} testid={`seller-band-${k}`} label={k} value={n.toLocaleString()} tone={k === "trusted" ? "emerald" : k === "good" ? "sky" : k === "fair" ? "amber" : "rose"} />
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2 mt-3 text-xs">
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P10</div><span className="tabular-nums font-medium">{s.p10}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P50</div><span className="tabular-nums font-medium">{s.p50}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">P90</div><span className="tabular-nums font-medium">{s.p90}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Min/Max</div><span className="tabular-nums">{s.min}/{s.max}</span></div>
              <div className="bg-slate-50 rounded px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Mean</div><span className="tabular-nums">{s.mean}</span></div>
            </div>
          </>
        ) : <div className="text-sm text-slate-500" data-testid="seller-empty">No seller trust scores yet.</div>}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 6 · INTEGRATIONS STATUS (rendered inside Trust > Vendors)
// ═════════════════════════════════════════════════════════════════════════
export function IntegrationsStatusCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/trust/integrations-status").then(r => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div className="rounded-2xl border bg-white overflow-hidden" data-testid="integrations-card">
      <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b flex items-center justify-between">
        <span className="flex items-center gap-1.5"><Plug className="w-4 h-4" /> Integrations · live vs mock</span>
        <div className="flex gap-2">
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="int-live">{data.live} live</Badge>
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid="int-mock">{data.mock} mock</Badge>
          {data.broken > 0 && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]" data-testid="int-broken">{data.broken} broken</Badge>}
        </div>
      </div>
      <div className="grid grid-cols-12 px-4 py-2 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500 border-b">
        <div className="col-span-3">Vendor</div>
        <div className="col-span-3">Purpose</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-2">Mode</div>
        <div className="col-span-2">Notes</div>
      </div>
      <div data-testid="integrations-table">
        {data.integrations.map(i => {
          const tone = i.mode === "live" ? "emerald"
            : i.mode === "configured_but_key_expired" ? "rose"
            : "amber";
          return (
            <div key={i.id} data-testid={`int-row-${i.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-3 font-medium">{i.name}</div>
              <div className="col-span-3 text-xs text-slate-600">{i.purpose}</div>
              <div className="col-span-2"><Badge variant="outline" className="text-[10px]">{i.category}</Badge></div>
              <div className="col-span-2"><Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{i.mode}</Badge></div>
              <div className="col-span-2 text-[11px] text-slate-500 truncate" title={i.notes || i.evidence}>{i.notes || "—"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 7 · UNDERWRITING METRICS
// ═════════════════════════════════════════════════════════════════════════
export function UnderwritingMetricsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/regulatory/underwriting-metrics").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="uw-loading">Loading…</div>;
  return (
    <div data-testid="uw-panel" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="uw-kpis">
        <Kpi testid="uw-apps"     label={`Applications · ${data.window_days}d`} value={data.applications.toLocaleString()} tone="slate" />
        <Kpi testid="uw-approval" label="Approval rate"   value={`${data.approval_rate_pct}%`} tone={data.approval_rate_pct >= 60 ? "emerald" : "amber"} />
        <Kpi testid="uw-auto"     label="Auto-decision"   value={`${data.auto_approve_pct}%`} sub="vs manual override" tone="slate" />
        <Kpi testid="uw-override" label="Manual override" value={`${data.manual_override_pct}%`} tone={data.manual_override_pct > 20 ? "amber" : "emerald"} />
      </div>
      <div className="rounded-2xl border bg-white p-4" data-testid="uw-aan-card">
        <div className="text-sm font-medium text-slate-700 mb-3">Adverse Action Notices (ECOA · Reg B)</div>
        <div className="grid grid-cols-4 gap-3">
          <Kpi testid="uw-aan-total"   label="AAN total"           value={data.aan.total.toLocaleString()} tone="slate" />
          <Kpi testid="uw-aan-decline" label="Decline"             value={data.aan.decline.toLocaleString()} tone={data.aan.decline ? "amber" : "emerald"} />
          <Kpi testid="uw-aan-reduced" label="Limit reduction"     value={data.aan.reduced_limit.toLocaleString()} tone="slate" />
          <Kpi testid="uw-aan-ontime"  label="Delivered ≤30d"      value={data.aan.delivery_within_30d_pct != null ? `${data.aan.delivery_within_30d_pct}%` : "—"} tone={data.aan.delivery_within_30d_pct >= 95 ? "emerald" : "rose"} />
        </div>
        <div className="text-xs text-slate-500 mt-3 italic">{data.ecoa_attestation}</div>
      </div>
    </div>
  );
}
